import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { formatPrice } from '@template-project/afrodita-components';
import { useIntl } from '@/lib/intl';
import type { CollectionPack } from '@/domain/entities/collection.entity';

const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 30px;
  padding: 0 10px;

  @media (max-width: 480px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    padding: 0;
  }
`;

const CardLink = styled(Link)`
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
  width: calc(33.333% - 20px);

  &:hover img {
    transform: scale(1.04);
  }

  @media (max-width: 900px) {
    width: calc(50% - 15px);
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const ImageWrap = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  aspect-ratio: 3 / 4;
  background: #f5f5f5;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.25s ease;
`;

const Placeholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 12px;
  color: #888;
`;

const Title = styled.h3`
  margin: 12px 0 6px;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
`;

const PriceRow = styled.p`
  margin: 0;
  text-align: center;
  font-size: 14px;
`;

const ComparePrice = styled.span`
  margin-right: 8px;
  color: #888;
  text-decoration: line-through;
`;

interface PackCollectionGridProps {
  packs: CollectionPack[];
  loading?: boolean;
}

export function PackCollectionGrid({ packs, loading }: PackCollectionGridProps) {
  const { localizedPath, countryConfig } = useIntl();
  const priceOptions = { currency: countryConfig.currency, locale: countryConfig.locale };

  if (loading && packs.length === 0) {
    return <p style={{ textAlign: 'center', padding: '2rem' }}>Loading packs...</p>;
  }

  if (!loading && packs.length === 0) {
    return <p style={{ textAlign: 'center', padding: '2rem' }}>No packs in this collection yet.</p>;
  }

  return (
    <Grid>
      {packs.map((pack) => {
        const priceCents = pack.priceMin?.display ?? pack.priceMin?.base;
        const compareCents = pack.compareAt?.display ?? pack.compareAt?.base;

        return (
          <CardLink key={pack.id} to={localizedPath(`/packs/${pack.slug}`)}>
            <ImageWrap>
              {pack.imageUri ? (
                <Image src={pack.imageUri} alt={pack.name} loading="lazy" />
              ) : (
                <Placeholder>No image</Placeholder>
              )}
            </ImageWrap>
            <Title>{pack.name}</Title>
            {priceCents != null && (
              <PriceRow>
                {compareCents != null && compareCents > priceCents && (
                  <ComparePrice>{formatPrice(compareCents, priceOptions)}</ComparePrice>
                )}
                <strong>{formatPrice(priceCents, priceOptions)}</strong>
              </PriceRow>
            )}
          </CardLink>
        );
      })}
    </Grid>
  );
}
